import { selector, selectorFamily } from 'recoil';
import {
  createArrayByUniqueKeySelectorFamily,
  createFieldSelector,
  createFieldSelectorFamily,
} from '@zurich/common/recoil';
import { vsgaStepConfig } from '~/components/stepConfig';
import type { AutoArea, StepNames } from '~/lib/types';
import { activeStepAtom, vsgaPayloadAtom } from './atoms';

const vsgaPayloadSelectorHelper = createFieldSelector(vsgaPayloadAtom);

export const firstNameSelector = vsgaPayloadSelectorHelper('firstName', {
  defaultValue: undefined,
});

export const lastNameSelector = vsgaPayloadSelectorHelper('lastName', {
  defaultValue: undefined,
});

export const postalCodeSelector = vsgaPayloadSelectorHelper('postalCode', {
  defaultValue: undefined,
});

export const licensePlateFieldSelector = vsgaPayloadSelectorHelper(
  'licenseplate',
  {
    defaultValue: undefined,
  }
);

export const dryAndCleanCarSelector = vsgaPayloadSelectorHelper(
  'isDryAndClean',
  {
    defaultValue: null,
  }
);

export const sunnyAndDryWeatherSelector = vsgaPayloadSelectorHelper(
  'isSunnyWeather',
  {
    defaultValue: null,
  }
);

export const enoughSpaceSelector = vsgaPayloadSelectorHelper('isEnoughSpace', {
  defaultValue: null,
});

export const timeBeforeSelector = vsgaPayloadSelectorHelper('isTimeBefore', {
  defaultValue: null,
});

export const confirmationOfCorrectAutoDataSelector = vsgaPayloadSelectorHelper(
  'confirmationOfCorrectAutoData',
  {
    defaultValue: null,
  }
);

export const confirmationOfCorrectPhotoDataSelector = vsgaPayloadSelectorHelper(
  'confirmationOfCorrectPhotoData',
  {
    defaultValue: null,
  }
);

export const emailFieldSelector = vsgaPayloadSelectorHelper('email', {
  defaultValue: undefined,
});

export const photosFieldSelector = vsgaPayloadSelectorHelper('photos', {
  defaultValue: [],
});

export const damagesFieldSelector = vsgaPayloadSelectorHelper('damages', {
  defaultValue: [],
});

export const autoPhotoUploadURLUniqueKeySelectorFamily =
  createArrayByUniqueKeySelectorFamily(photosFieldSelector, 'id', {
    defaultValue: {},
  });

export const damagePhotoUploadURLUniqueKeySelectorFamily =
  createArrayByUniqueKeySelectorFamily(damagesFieldSelector, 'id', {
    defaultValue: {},
  });

export const autoPhotoFieldUniqueKeySelectorFamily = createFieldSelectorFamily(
  autoPhotoUploadURLUniqueKeySelectorFamily
);

export const damagePhotoUploadURLUniqueKeyFieldSelectorFamily =
  createFieldSelectorFamily(damagePhotoUploadURLUniqueKeySelectorFamily);

export const descriptionOfDamageFieldSelectorFamily =
  damagePhotoUploadURLUniqueKeyFieldSelectorFamily('descriptionOfDamage', {
    defaultValue: undefined,
  });

export const typeOfDamageFieldSelectorFamily =
  damagePhotoUploadURLUniqueKeyFieldSelectorFamily('typeOfDamage', {
    defaultValue: undefined,
  });

export const areaOfDamageFieldSelectorFamily =
  damagePhotoUploadURLUniqueKeyFieldSelectorFamily('areaOfDamage', {
    defaultValue: undefined,
  });

export const damageDocumentDataFieldSelectorFamily =
  damagePhotoUploadURLUniqueKeyFieldSelectorFamily('fileId', {
    defaultValue: undefined,
  });

export const activeStepSelector = selector<StepNames>({
  key: '@vsga/activeStepSelector',
  get: ({ get }) => {
    const activeStep = get(activeStepAtom);
    return vsgaStepConfig[activeStep].stepName;
  },
  set: ({ set }, value) => {
    const step = vsgaStepConfig.findIndex((step) => step.stepName === value);
    const activeStepNumber = step >= 0 ? step : 0;

    set(activeStepAtom, activeStepNumber);
  },
});

export const numberOfDamagesPerStepSelector = selectorFamily<
  number,
  AutoArea | undefined
>({
  key: '@vsga/numberOfDamagesPerStepSelector',
  get:
    (area) =>
    ({ get }) => {
      if (!area) {
        return 0;
      }
      const damages = get(damagesFieldSelector);

      return damages.filter((damage) => damage.area === area).length;
    },
});
